import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { filter, find, some } from 'lodash';
import { createElement, FC, Fragment, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PerformanceReport,
  PerformanceReportAdAccount,
  PerformanceReportDetailRequest,
  PerformanceReportDimension,
  PerformanceReportNetwork,
  PerformanceReportPeriodPattern,
  PerformanceReportSummaryRequest,
  PeriodicDetailPerformanceWithAllFieldsFragment,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
  ProjectDetailsForShowFragment,
} from '../../../generated/graphql';
import _AmazonReportingIntegrationBanner from '../../component/compound/AmazonReportingIntegrationBanner';
import DetailPerformanceSection from '../../component/compound/CreativePerformanceReport/DetailPerformanceSection';
import ImprovementRateSection from '../../component/compound/CreativePerformanceReport/ImprovementRateSection';
import ReportControl from '../../component/compound/CreativePerformanceReport/ReportControl';
import _FacebookReportingIntegrationBanner from '../../component/compound/FacebookReportingIntegrationBanner';
import _GoogleReportingIntegrationBanner from '../../component/compound/GoogleReportingIntegrationBanner';
import PerformanceReportExportingDialog from '../../component/compound/PerformanceReportExportingDialog';
import Spinner from '../../component/compound/Spinner';
import { SPACING_HUGE, SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  project: ProjectDetailsForShowFragment | null;
  performanceReports: Map<
    string,
    {
      report: PerformanceReport;
      request: PerformanceReportSummaryRequest;
      dailySummaryPerformanceFetching: boolean;
      dailySummaryPerformance: DailySummaryPerformanceWithAllFieldsFragment[] | null;
      periodicDetailPerformanceFetching: boolean;
      periodicDetailPerformance: PeriodicDetailPerformanceWithAllFieldsFragment | null;
      periodicImprovementPerformanceFetching: boolean;
      periodicImprovementPerformance: PeriodicImprovementPerformanceWithAllFieldsFragment | null;
    }
  >;
  onReportRequestChange: (report: PerformanceReport, request: PerformanceReportSummaryRequest) => void;
  onSummaryRequestChange: (report: PerformanceReport, request: PerformanceReportSummaryRequest) => void;
  onRequestExporting: (
    report: PerformanceReport,
    request: PerformanceReportDetailRequest,
    dimension: PerformanceReportDimension,
  ) => void;
  onIntegrationBannerClose: (adPlatform: PerformanceReportNetwork) => void;
  isIntegrationBannerVisible: (adPlatform: PerformanceReportNetwork) => boolean;
  // onGotoAnalyticsPage: () => void;
  loading?: boolean;
  className?: string;
}

const Reporting: FC<Props> = ({
  loading,
  project,
  performanceReports,
  onReportRequestChange,
  onSummaryRequestChange,
  onIntegrationBannerClose,
  onRequestExporting,
  isIntegrationBannerVisible,
  className,
}) => {
  const reportIds = useMemo(() => Array.from(performanceReports.keys()), [performanceReports]);
  const flattenReports = useMemo(
    () =>
      filter(Array.from(performanceReports.values()), (report) => report.report.adAccount.network != 'yahoo').map(
        (report) => report.report,
      ),
    [performanceReports],
  );
  const adAccounts = useMemo(() => flattenReports.map((report) => report.adAccount), [flattenReports]);
  const defaultReportId = useMemo(() => reportIds[0], [reportIds]);
  const defaultAdAccount = useMemo(() => adAccounts[0] || null, [adAccounts]);

  const [closedIntegrationBanners, setClosedIntegrationBanners] = useState(
    (['facebook', 'google', 'amazon'] as PerformanceReportNetwork[]).filter(
      (adPlatform) => !isIntegrationBannerVisible(adPlatform),
    ),
  );
  const [selectedReportId, setSelectedReportId] = useState(defaultReportId);
  const [adAccount, setAdAccount] = useState<PerformanceReportAdAccount | null>(defaultAdAccount);

  const selectedReport = useMemo(() => performanceReports.get(selectedReportId), [
    performanceReports,
    selectedReportId,
  ]);

  const defaultPeriod = useMemo(() => (selectedReport ? selectedReport.request.period : 'last_7_days'), [
    selectedReport,
  ]);
  const [period, setPeriod] = useState<PerformanceReportPeriodPattern>(defaultPeriod);

  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    setSelectedReportId(defaultReportId);
  }, [defaultReportId]);

  useEffect(() => {
    setAdAccount(defaultAdAccount);
  }, [defaultAdAccount]);

  useEffect(() => {
    setPeriod(defaultPeriod);
  }, [defaultPeriod]);

  const onPeriodChange = (period: PerformanceReportPeriodPattern) => {
    setPeriod(period);
    if (selectedReport) {
      const { report, request } = selectedReport;
      onReportRequestChange(report, { ...request, period });
    }
  };

  const onAdAccountChange = (accountId: string) => {
    const targetReport = find(
      Array.from(performanceReports.values()),
      (report) => report.report.adAccount.accountId === accountId,
    );

    if (targetReport) {
      setAdAccount(targetReport.report.adAccount);
      setSelectedReportId(targetReport.report.id);
      onReportRequestChange(targetReport.report, {
        ...targetReport.request,
        period,
      });
    }
  };

  const toggleDialog = () => {
    setShowDialog((prevShowDialog) => !prevShowDialog);
  };

  if (loading || project === null) {
    return (
      <Root className={className}>
        <Spinner />
      </Root>
    );
  }

  const lastUpdatedHeader = () => {
    if (selectedReport) {
      return (
        <ReportStatus>
          <FormattedDate value={selectedReport.report.lastUpdatedAt} year="numeric" month="numeric" day="numeric">
            {(date: string) => (
              <FormattedMessage id="performance_report_card.header.last_updated_at" values={{ date }} />
            )}
          </FormattedDate>
        </ReportStatus>
      );
    }
    return null;
  };

  const hasFacebookIntegration = some(
    Array.from(performanceReports.values()),
    (report) => report.report.adAccount.network === 'facebook',
  );
  const hasGoogleIntegration = some(
    Array.from(performanceReports.values()),
    (report) => report.report.adAccount.network === 'google',
  );
  const hasAmazonIntegration = some(
    Array.from(performanceReports.values()),
    (report) => report.report.adAccount.network === 'amazon',
  );

  return (
    <Root className={className}>
      {!hasFacebookIntegration && !closedIntegrationBanners.includes('facebook') && (
        <FacebookReportingIntegrationBanner
          onClose={() => {
            setClosedIntegrationBanners([...closedIntegrationBanners, 'facebook']);
            onIntegrationBannerClose('facebook');
          }}
        />
      )}
      {!hasGoogleIntegration && !closedIntegrationBanners.includes('google') && (
        <GoogleReportingIntegrationBanner
          onClose={() => {
            setClosedIntegrationBanners([...closedIntegrationBanners, 'google']);
            onIntegrationBannerClose('google');
          }}
        />
      )}
      {!hasAmazonIntegration && !closedIntegrationBanners.includes('amazon') && (
        <AmazonReportingIntegrationBanner
          onClose={() => {
            setClosedIntegrationBanners([...closedIntegrationBanners, 'amazon']);
            onIntegrationBannerClose('amazon');
          }}
        />
      )}

      {adAccounts.length >= 1 && (
        <HeadingContainer>
          <Heading variant="h5" color="textSecondary">
            <FormattedMessage id="performance_report_headline.improvement_rate" />
            {lastUpdatedHeader()}
          </Heading>
          <ReportControl
            period={period}
            onPeriodChange={onPeriodChange}
            adAccount={adAccount}
            adAccounts={adAccounts}
            onAdAccountChange={onAdAccountChange}
          />
        </HeadingContainer>
      )}

      {selectedReport && (
        <>
          <PerformanceReportExportingDialog
            key={selectedReport.report.id}
            report={selectedReport.report}
            open={showDialog}
            onRequestClose={toggleDialog}
            onClickSubmitButton={(_, dimension) => {
              toggleDialog();
              onRequestExporting(selectedReport.report, selectedReport.request, dimension);
            }}
          />

          <ImprovementRateSection
            adAccount={adAccount}
            period={period}
            request={selectedReport.request}
            onRequestChange={(request: PerformanceReportSummaryRequest) => {
              onSummaryRequestChange(selectedReport.report, request);
            }}
            periodicImprovementPerformance={selectedReport.periodicImprovementPerformance}
            periodicImprovementPerformanceFetching={selectedReport.periodicImprovementPerformanceFetching}
            dailySummaryPerformance={selectedReport.dailySummaryPerformance}
            dailySummaryPerformanceFetching={selectedReport.dailySummaryPerformanceFetching}
          />
          <DetailPerformanceSection
            report={selectedReport.report}
            periodicDetailPerformance={selectedReport.periodicDetailPerformance}
            periodicDetailPerformanceFetching={selectedReport.periodicDetailPerformanceFetching}
            onClickExportButton={toggleDialog}
          />
        </>
      )}
    </Root>
  );
};

const Root = styled('div')();

const integrationBannerBackgroundColor = '#fffbd8';

const Heading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const HeadingContainer = styled('div')({
  ...flexibleLayoutContainer({
    justifyContent: 'space-between',
    direction: 'row',
  }),

  [mq('sp')]: {
    ...flexibleLayoutContainer({
      justifyContent: 'normal',
      direction: 'column',
    }),
  },
});

const FacebookReportingIntegrationBanner = styled(_FacebookReportingIntegrationBanner)({
  marginBottom: SPACING_REGULAR,
  background: integrationBannerBackgroundColor,
});

const GoogleReportingIntegrationBanner = styled(_GoogleReportingIntegrationBanner)({
  marginBottom: SPACING_REGULAR,
  background: integrationBannerBackgroundColor,
});

const AmazonReportingIntegrationBanner = styled(_AmazonReportingIntegrationBanner)({
  marginBottom: SPACING_REGULAR,
  background: integrationBannerBackgroundColor,
});

const ReportStatus = styled(Typography)({
  ...gridLayoutItem('reportStatus'),
  marginLeft: SPACING_HUGE,
});
ReportStatus.defaultProps = { variant: 'caption' };

export default Reporting;
